import * as Allocation from '@digitalasset/cn-allocation-api-ts-client';
import * as TokenMetadata from '@digitalasset/cn-token-metadata-api-ts-client';
import React, { useMemo } from 'react';
import { BaseApiMiddleware } from '../utils/BaseApiMiddleware';
import { OpenAPILoggingMiddleware } from '../utils/OpenApiLoggingMiddleware';
import { CNAllocationApiContext } from './CNAllocationApiContext';
import { CNAllocationApiData } from './CNAllocationApiTypes';
import { useUserState } from './UserContext';

export type CNAllocationApiProviderProps = {
  url: string;
};

export const CNAllocationApiProvider = ({
  url,
  children,
}: React.PropsWithChildren<CNAllocationApiProviderProps>) => {
  const { loggedInUser } = useUserState();
  const selectedToken = loggedInUser?.userAccessToken ?? '';

  const allocationClient: Allocation.DefaultApi | undefined = useMemo(() => {
    const configuration = Allocation.createConfiguration({
      baseServer: new Allocation.ServerConfiguration(`${url}/v0/scan-proxy`, {}),
      promiseMiddleware: [
        new BaseApiMiddleware<Allocation.RequestContext, Allocation.ResponseContext>(selectedToken),
        new OpenAPILoggingMiddleware('scan-proxy'),
      ],
    });

    return new Allocation.DefaultApi(configuration);
  }, [selectedToken, url]);

  const tokenMetadataClient: TokenMetadata.DefaultApi | undefined = useMemo(() => {
    const configuration = TokenMetadata.createConfiguration({
      // TODO: /v0/scan-proxy part should come from somewhere
      baseServer: new TokenMetadata.ServerConfiguration(`${url}/v0/scan-proxy`, {}),
      promiseMiddleware: [
        new BaseApiMiddleware<TokenMetadata.RequestContext, TokenMetadata.ResponseContext>(
          selectedToken,
        ),
        new OpenAPILoggingMiddleware('scan-proxy'),
      ],
    });

    return new TokenMetadata.DefaultApi(configuration);
  }, [selectedToken, url]);

  const data: CNAllocationApiData | undefined = useMemo(() => {
    if (!allocationClient || !tokenMetadataClient) {
      return undefined;
    }

    const result: CNAllocationApiData = {
      allocationClient,
      tokenMetadataClient,
    };

    return result;
  }, [allocationClient, tokenMetadataClient]);

  return <CNAllocationApiContext.Provider value={data}>{children}</CNAllocationApiContext.Provider>;
};
